import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogTitle, MatDialogClose, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EventService } from '../../../services/api/EventService';
import { EventStatusService } from '../../../services/api/EventStatusService';
import { forkJoin, of, switchMap } from 'rxjs';
import { EventStatusModel } from '../../../models/api/EventStatusModel';
import { EventModel } from '../../../models/api/EventModel';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import 'moment/locale/en-gb';
import { MatMomentDateModule, provideMomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
import { A11yModule } from "@angular/cdk/a11y";

@Component({
  selector: 'app-event-details',
  standalone: true,
  imports: [
    FormsModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatButtonModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatDialogContent,
    MatDialogTitle,
    MatDialogClose,
    A11yModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-gb' },
    provideMomentDateAdapter(),
  ],
  templateUrl: './event-details.component.html',
  styleUrl: './event-details.component.scss'
})
export class EventDetailsComponent implements OnInit {

  public eventID: number | null = null;
  public eventForm!: FormGroup;
  public eventStatuses: EventStatusModel[] = [];
  public eventDetail: EventModel = new EventModel;
  public isLoaded: boolean = false;

  constructor(
    private fb: FormBuilder,
    private eventService: EventService,
    private dialogRef: MatDialogRef<EventDetailsComponent>,
    private snackbar: MatSnackBar,
    private eventStatusService: EventStatusService,
    @Inject(MAT_DIALOG_DATA) public data?: { eventID: number }
  ) {
    if (data?.eventID) {
      this.eventID = data?.eventID;
    }

  }

  ngOnInit(): void {
    if (this.eventID && this.eventID > 0){
      this.buildEditForm();
    } else {
      this.buildAddForm();
    }
    this.getEventDetails();

  }

  private buildEditForm() {
    this.eventForm = this.fb.group({
      eventID: [null],
      eventName: ['', [Validators.required, Validators.minLength(2)]],
      venueName: ['', [Validators.required, Validators.minLength(2)]],
      address: [''],
      city: ['', [Validators.required, Validators.minLength(2)]],
      country: ['', [Validators.required, Validators.minLength(2)]],
      startDate: [new Date(), [Validators.required]],
      endDate: [new Date(), [Validators.required]],
      eventStatusID: [0],
      active: [0],
    });
  }

    private buildAddForm() {
    this.eventForm = this.fb.group({
      eventName: ['', [Validators.required, Validators.minLength(2)]],
      venueName: ['', [Validators.required, Validators.minLength(2)]],
      address: [''],
      city: ['', [Validators.required, Validators.minLength(2)]],
      country: ['', [Validators.required, Validators.minLength(2)]],
      startDate: [new Date(), [Validators.required]],
      endDate: [new Date(), [Validators.required]],
      eventStatusID: [0],
      active: [0],
    });
  }

  getEventDetails() {
    this.eventStatusService.findEventStatus().pipe(
      switchMap(statusesRes => {
        this.eventStatuses = statusesRes.data as EventStatusModel[];
        if (!this.eventID) {
          this.isLoaded = true;
          return of(null);
        }
        return this.eventService.getEvent(this.eventID);
      })
    ).subscribe({
      next: (event) => {
        if (event) {
          this.eventDetail = event;
          this.populateEventDetail(event);
          this.isLoaded = true;
        }
      },
      error: (err) => console.error('API error:', err)
    });
  }

  populateEventDetail(event: EventModel) {
    this.eventForm.patchValue({
      eventID: event.eventID,
      eventName: event.eventName,
      venueName: event.venueName,
      address: event.address,
      city: event.city,
      country: event.country,
      startDate: event.startDate,
      endDate: event.endDate,
      eventStatusID: event.eventStatusID,
      active: event.active,
    })
  }

  updateEvent() {
    if (this.eventForm.valid) {

      let eventDetail: EventModel = { ...this.eventForm.value };

      eventDetail.startDate = toUtcDate(this.eventForm.value.startDate)!;
      const endDate = new Date(this.eventForm.value.endDate);
    endDate.setHours(23, 59, 59, 999);
    eventDetail.endDate = toUtcDate(endDate)!;
      eventDetail.createdDate = new Date();
      eventDetail.active = true;

      this.eventService.updateEvent(this.eventID!, eventDetail).subscribe({
        next: (data) => {
          this.snackbar.open("EVENT UPDATED", "CLOSE", {
            panelClass: "confirm-snackbar",
          });
          this.dialogRef.close();
        },
        error: (err) => {
        }
      })
    }
  }

  addEvent() {
    if (this.eventForm.valid) {

      let eventDetail: EventModel = this.eventForm.value;
      eventDetail.startDate = toUtcDate(this.eventForm.value.startDate)!;
      const endDate = new Date(this.eventForm.value.endDate);
    endDate.setHours(23, 59, 59, 999);
    eventDetail.endDate = toUtcDate(endDate)!;
      eventDetail.createdDate = new Date();
      eventDetail.active = true;

      this.eventService.createEvent(eventDetail).subscribe({
        next: (data) => {
          this.snackbar.open("EVENT CREATED", "CLOSE", {
            panelClass: "confirm-snackbar",
          });
          this.dialogRef.close();

        },
        error: (err) => {
        }
      })
    }
  }

}


function toUtcDate(d: Date | moment.Moment | null | undefined): Date | null {
  if (!d) return null;

  const m = moment(d);
  if (!m.isValid()) return null;

  return new Date(Date.UTC(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second()));
}